import React, { useEffect, useRef } from 'react';
import { ArrowLeftIcon, CheckIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { contact, founder } from '../data/content';
import { track } from '../utils/tracking';
const nextSteps = [{
  title: 'We review what you sent',
  body: 'Phil and the NEXT leadership team read every submission personally. Nothing goes into a mass drip.'
}, {
  title: 'You hear from us within one business day',
  body: 'A short text or call to find a time that actually works around your showings and closings.'
}, {
  title: 'A private, no-pressure conversation',
  body: 'Your business, your goals, and where better systems, marketing, or support could move the needle.'
}];

/** Confirmation page for form submissions. Fires the Meta `Lead` conversion on view. */
export function ThankYou() {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    track('form_submission', {
      location: 'thank_you_page'
    });
  }, []);
  return <div className="flex min-h-screen w-full flex-col bg-white" data-id="element-261" data-mp="283jiboq928gm">
      <header className="w-full border-b border-line" data-id="element-262" data-mp="2361eudv04lg0">
        <div className="mx-auto flex h-14 max-w-content items-center px-4 md:h-[68px] md:px-8" data-id="element-263" data-mp="1y7gemefr21rk">
          <a href="/" className="flex items-center" aria-label="HomeSmart NEXT home" data-id="element-264" data-mp="1u6egy21hdxrp">
            <Logo data-id="element-265" data-mp="2qwdyglzm06mf" />
          </a>
        </div>
      </header>

      <main className="mx-auto w-full max-w-content flex-1 px-4 py-14 md:px-8 md:py-20" data-id="element-266" data-mp="2qvvvl6ifyu5j">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16" data-id="element-267" data-mp="2lylu2shs2s1c">
          <div data-id="element-268" data-mp="2h5spq5q81lps">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft" data-id="element-269" data-mp="2h59jvnduins4">
              <CheckIcon className="h-5 w-5 text-accent" aria-hidden="true" data-id="element-270" data-mp="2d47jv4c3u7p3" />
            </span>

            <h1 className="mt-5 font-display text-[30px] font-semibold leading-[1.08] tracking-[-0.03em] text-ink sm:text-[40px] lg:text-[46px]" data-id="element-271" data-mp="39nolut64m19k">
              Your request is in.
              <span className="block text-accent" data-id="element-272" data-mp="12ycwaeq1oynj">Let&rsquo;s find a time to talk.</span>
            </h1>

            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-ink-600 md:mt-6 md:text-[17px]" data-id="element-273" data-mp="17wo46jws4z34">
              Thanks for reaching out to HomeSmart NEXT. This is a private conversation about your business, not a
              recruiting pitch and not a mailing list.
            </p>

            <ol className="mt-8 space-y-5 border-l border-line pl-6" data-id="element-274" data-mp="k7vf97ekrri0">
              {nextSteps.map(step => <li key={step.title} className="relative" data-id="element-275" data-mp="p7zoqdh2flyq">
                  <span className="absolute -left-[26px] top-[7px] h-2 w-2 rounded-full bg-accent" aria-hidden="true" data-id="element-276" data-mp="aa78tk7lwvk0" />
                  <p className="font-display text-[15px] font-semibold text-ink md:text-[16px]" data-id="element-277" data-mp="favar0xdiwt0">{step.title}</p>
                  <p className="mt-1 text-[14px] leading-relaxed text-ink-600" data-id="element-278" data-mp="29n2ynuba2dfa">{step.body}</p>
                </li>)}
            </ol>

            <div className="mt-9 flex flex-col gap-3 border-t border-line pt-6 sm:flex-row sm:gap-8" data-id="element-279" data-mp="3u5vntb98lw23">
              <a href={`tel:${contact.phone}`} onClick={() => track('phone_click', {
              location: 'thank_you'
            })} className="flex items-center gap-2 text-[15px] font-medium text-ink transition-colors duration-150 ease-smooth hover:text-accent" data-id="element-280" data-mp="3p7bxyzriw04g">
                <PhoneIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-281" data-mp="gomn16kjyi3p" />
                Call or text: {contact.phone}
              </a>
              <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-[15px] font-medium text-ink transition-colors duration-150 ease-smooth hover:text-accent" data-id="element-282" data-mp="qodk9g0hzb7h">
                <MailIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-283" data-mp="2seyqumhhh79k" />
                {contact.email}
              </a>
            </div>

            <a href="/" className="mt-8 inline-flex items-center gap-2 text-[14px] font-medium text-ink-600 transition-colors duration-150 ease-smooth hover:text-ink" data-id="element-284" data-mp="2nirtvj72f63r">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-285" data-mp="2tyql8nadenl0" />
              Back to HomeSmart NEXT
            </a>
          </div>

          <figure className="rounded-2xl border border-line bg-canvas p-6 md:p-8" data-id="element-286" data-mp="33vd63c2p5en1">
            <div className="relative h-20 w-20 overflow-hidden rounded-2xl bg-white md:h-[104px] md:w-[104px]" data-id="element-287" data-mp="267uwy054iaii">
              <img src={founder.image} alt={`${founder.name}, ${founder.title} of HomeSmart NEXT`} loading="eager" decoding="async" className="absolute left-[-33%] top-0 w-[190%] max-w-none" data-id="element-288" data-mp="2b5mp8v9ojyn8" />
            </div>
            <blockquote className="mt-5 font-display text-[17px] leading-[1.5] tracking-[-0.01em] text-ink md:text-[19px]" data-id="element-289" data-mp="1nhmuzld5tq3w">
              &ldquo;I read every one of these myself. Looking forward to hearing about your business and where you want
              to take it.&rdquo;
            </blockquote>
            <figcaption className="mt-3 text-[13px] font-medium text-ink-400" data-id="element-290" data-mp="5k9t37jf3fcb">
              <span className="text-ink" data-id="element-291" data-mp="jm2uo30j2j5">{founder.name}</span> — {founder.title}, {founder.company}
            </figcaption>
          </figure>
        </div>
      </main>

      <footer className="w-full border-t border-line" data-id="element-292" data-mp="3rsswdt13pzrj">
        <div className="mx-auto max-w-content px-4 py-6 text-[12px] leading-relaxed text-ink-400 md:px-8" data-id="element-293" data-mp="3msdi8z8zwo8i">
          <p data-id="element-294" data-mp="3hztz7mrjh0wb">HomeSmart NEXT is a real estate team affiliated with HomeSmart.</p>
          <p className="mt-2" data-id="element-295" data-mp="3dy8l5y39qgkj">© {new Date().getFullYear()} HomeSmart NEXT. All rights reserved.</p>
        </div>
      </footer>
    </div>;
}